<script>
import { env } from '@shared/env';
import talkizyMixin from '@app/mixins/talkizy';
import TalkizyAPI from '@app/services/API/Talkizy';
import talkizyErrorMessage from '@app/utils/talkizyErrors';
import TalkizyConnect from '@app/components/TalkizyConnect.vue';
import TalkizyDisconnectSection from '@app/components/TalkizyDisconnectSection.vue';
import TalkizyJoinTeam from '@app/components/TalkizyJoinTeam.vue';

// Capacités chiffrées du plan Talkizy (source : config/plans.php côté Talkizy).
const PLAN_THRESHOLDS = [
  { key: 'max_participants', label: 'Nombre de participants par classe virtuelle' },
  { key: 'max_webinar_participants', label: 'Nombre de participants en webinaire' },
  { key: 'max_meeting_duration', label: 'Durée maximale d\'une classe virtuelle', format: 'duration' },
  { key: 'max_meetings_per_month', label: 'Nombre de classes virtuelles par mois' },
  { key: 'max_permanent_rooms', label: 'Nombre de salles permanentes' },
  { key: 'recording_retention_days', label: 'Durée de conservation des enregistrements', unit: 'jours' },
  { key: 'max_contacts', label: 'Nombre total de contacts' },
  { key: 'max_spaces', label: 'Nombre total d\'espaces' },
  { key: 'max_team_members', label: 'Nombre total de membres d\'équipe' },
  {
    key: 'support_level',
    label: 'Support',
    map: { community: 'Communauté', email: 'Par email', priority: 'Prioritaire' },
  },
];

// Fonctionnalités (oui/non) du plan Talkizy.
const PLAN_FEATURES = [
  { key: 'can_webinar', label: 'Webinaire' },
  { key: 'can_recording', label: 'Enregistrement des sessions' },
  { key: 'can_transcription', label: 'Transcription' },
  { key: 'can_analytics', label: 'Statistiques détaillées' },
  { key: 'can_get_signature', label: 'Émargement par signature' },
  { key: 'can_get_feedback', label: 'Enquête de satisfaction' },
  { key: 'qualiopi_compliance', label: 'Conformité Qualiopi' },
  { key: 'can_custom_domain', label: 'Domaine personnalisé' },
  { key: 'can_sso', label: 'Authentification SSO' },
  { key: 'can_white_label', label: 'Marque blanche' },
];

export default {
  name: 'ClasseVirtuelleParametres',
  components: { TalkizyConnect, TalkizyDisconnectSection, TalkizyJoinTeam },
  mixins: [talkizyMixin],
  head: {
    title: 'Classe virtuelle — Paramètres',
  },
  data() {
    return {
      // Plan Talkizy détaillé (issu de GET /talkizy/plan) : distinct du computed
      // `talkizyPlan` du mixin ({ label, color }), d'où le nom différent pour
      // éviter la collision data/computed.
      talkizyPlanDetail: null,
      // Compte Talkizy connecté + espace actif + espaces disponibles.
      teamContext: null,
      selectedTeamId: null,
      isSwitchingTeam: false,
    };
  },
  computed: {
    talkizyURL() {
      return env.talkizyURL;
    },
    planThresholds() {
      const caps = (this.talkizyPlanDetail && this.talkizyPlanDetail.capabilities) || {};
      return PLAN_THRESHOLDS
        .filter((item) => caps[item.key] !== undefined && caps[item.key] !== null)
        // La durée de conservation des enregistrements n'a de sens que si le
        // forfait permet d'enregistrer (can_recording).
        .filter((item) => item.key !== 'recording_retention_days' || !!caps.can_recording)
        .map((item) => ({
          key: item.key,
          label: item.label,
          display: this.formatCapabilityValue(caps[item.key], item),
        }));
    },
    planFeatures() {
      const caps = (this.talkizyPlanDetail && this.talkizyPlanDetail.capabilities) || {};
      return PLAN_FEATURES.map((item) => ({
        key: item.key,
        label: item.label,
        included: !!caps[item.key],
      }));
    },
    // Les forfaits teachizy_pro / teachizy_expert sont des offres Talkizy DÉBLOQUÉES
    // par l'abonnement Teachizy PRO/EXPERT (config teachizy_plans.php côté Talkizy),
    // et non souscrites directement sur Talkizy. On expose le palier Teachizy
    // correspondant (PRO/EXPERT) pour l'expliciter dans le wording ; null sinon.
    teachizyGrantedTier() {
      const plan = this.talkizyPlanDetail && this.talkizyPlanDetail.plan;
      if (plan === 'teachizy_pro') return 'PRO';
      if (plan === 'teachizy_expert') return 'EXPERT';
      return null;
    },
    // Un admin non membre de l'équipe n'a rien à voir des paramètres de l'espace :
    // il est invité à rejoindre l'équipe à la place.
    showJoinTeam() {
      return this.isAdminRole && !this.isMember && this.ownerConnected;
    },
    // `GET /talkizy/plan` est derrière le middleware `talkizy.member` : sans cette
    // garde la requête part en 403 pour un admin non membre.
    canSeePlan() {
      return this.ownerConnected && this.isMember;
    },
    talkizyAccount() {
      return this.teamContext ? this.teamContext.account : null;
    },
    activeTeam() {
      return this.teamContext ? this.teamContext.active_team : null;
    },
    teamOptions() {
      return (this.teamContext && this.teamContext.teams) || [];
    },
    // Seul le owner peut changer d'espace, et seulement s'il en a plusieurs.
    canSwitchTeam() {
      return !!(this.teamContext && this.teamContext.can_switch_team)
        && this.teamOptions.length > 1;
    },
    // Changer d'espace est bloqué tant que l'espace actif a des classes en cours
    // ou à venir (elles deviendraient inaccessibles depuis Teachizy).
    activeTeamHasPendingMeetings() {
      return !!(this.teamContext && this.teamContext.active_team_has_pending_meetings);
    },
  },
  watch: {
    // Le plan n'est chargé qu'une fois l'espace connecté et l'utilisateur membre
    // (à l'arrivée, après login, ou après avoir rejoint l'équipe).
    canSeePlan: {
      immediate: true,
      handler(can) {
        if (can) {
          if (!this.talkizyPlanDetail) this.fetchPlan();
          if (!this.teamContext) this.fetchTeamContext();
        }
      },
    },
  },
  mounted() {
    // Alimente isAdminRole / isMember : sans ça un admin verrait les boutons de
    // connexion actifs et la branche « espace connecté » à la place de l'invitation.
    this.fetchTalkizyStatus();
  },
  methods: {
    async fetchPlan() {
      try {
        this.talkizyPlanDetail = await TalkizyAPI.getPlan();
      } catch (e) {
        // Encart informatif : en cas d'échec on ne l'affiche simplement pas.
        this.talkizyPlanDetail = null;
      }
    },
    async fetchTeamContext() {
      try {
        this.teamContext = await TalkizyAPI.getTeamContext();
        this.selectedTeamId = this.activeTeam ? this.activeTeam.id : null;
      } catch (e) {
        this.teamContext = null;
      }
    },
    confirmSwitchTeam() {
      const team = this.teamOptions.find((t) => t.id === this.selectedTeamId);
      if (!team
        || (this.activeTeam && team.id === this.activeTeam.id)
        || this.activeTeamHasPendingMeetings) {
        return;
      }
      this.$buefy.dialog.confirm({
        title: 'Changer d\'espace actif',
        message: `Vous allez définir <strong>${team.name}</strong> comme espace actif. `
          + 'Les classes virtuelles créées et affichées dans Teachizy seront désormais celles du '
          + 'nouvel espace, et celles de l\'espace actuel ne seront plus accessibles depuis Teachizy. '
          + 'Cette action est impactante.',
        type: 'is-danger',
        confirmText: 'Changer d\'espace',
        cancelText: 'Annuler',
        onConfirm: () => this.switchTeam(team.id),
      });
    },
    async switchTeam(teamId) {
      this.isSwitchingTeam = true;
      try {
        await TalkizyAPI.switchTeam(teamId);
        this.$buefy.toast.open({ message: 'Espace actif mis à jour.', type: 'is-success' });
        // Le plan et le contexte dépendent de l'espace actif : on les recharge.
        this.talkizyPlanDetail = null;
        await this.fetchTeamContext();
        await this.fetchPlan();
      } catch (e) {
        const message = talkizyErrorMessage(e, 'Le changement d\'espace a échoué.');
        this.$buefy.toast.open({ message, type: 'is-danger' });
        // Réaligne le select et rafraîchit le contexte (l'état « en cours / à
        // venir » a pu changer) pour re-bloquer si besoin.
        this.selectedTeamId = this.activeTeam ? this.activeTeam.id : null;
        await this.fetchTeamContext();
      } finally {
        this.isSwitchingTeam = false;
      }
    },
    // 9999 / -1 = illimité (convention config/plans.php côté Talkizy).
    formatCapabilityValue(value, item) {
      if (item.map) {
        return item.map[value] || value;
      }
      if (typeof value === 'number') {
        if (value === -1 || value >= 9999) {
          return 'Illimité';
        }
        if (item.format === 'duration') {
          return this.formatDuration(value);
        }
        return item.unit ? `${value} ${item.unit}` : `${value}`;
      }
      return `${value}`;
    },
    // Durée en minutes : « 45 min » jusqu'à 59, « 1 h » / « 1 h 30 » à partir de 60.
    formatDuration(minutes) {
      if (minutes < 60) {
        return `${minutes} min`;
      }
      const hours = Math.floor(minutes / 60);
      const remainingMinutes = minutes % 60;
      return remainingMinutes ? `${hours} h ${remainingMinutes}` : `${hours} h`;
    },
  },
};
</script>

<template>
  <div class="cvparams">

    <GoProWrapper plan-lock="PRO" :has-sep="false">
      <!-- `isAdminRole` et `isMember` ne sont connus qu'une fois /talkizy/status
           répondu : sans cette attente, la branche « espace connecté » s'afficherait
           un instant avant de laisser place à l'invitation à rejoindre. -->
      <b-skeleton v-if="!hasFetchedStatus" height="120" :count="2" :animated="true" />

      <div v-else-if="showJoinTeam" class="cvparams_connected">
        <TalkizyJoinTeam
          :can-join="canJoin"
          :is-loading="isLoading"
          @join="openJoin"
        />
      </div>

      <TalkizyConnect
        v-else-if="!ownerConnected"
        :is-loading="isLoading"
        :is-login-loading="isLoginLoading"
        :disabled="isAdminRole"
        @register="openRegistration"
        @login="openLogin"
      />

      <div v-else class="cvparams_connected">
        <b-notification
          v-if="showUpgradeBanner"
          type="is-warning is-light"
          :closable="false"
          class="mb-5"
        >
          <div class="is-flex is-align-items-center is-justify-content-space-between is-flex-wrap-wrap">
            <span>
              Votre abonnement Teachizy+ <strong>{{ teachPlan }}</strong> vous donne accès au forfait
              Talkizy <strong>{{ talkizyPlan.label }}</strong>.
            </span>
            <b-button
              type="is-warning"
              size="is-small"
              :loading="isLoginLoading"
              class="ml-4"
              @click="openLogin"
            >
              Activer maintenant
            </b-button>
          </div>
        </b-notification>

        <div class="box">
          <div class="content">
            <h2 class="is-size-6">
              Votre espace est connecté à Talkizy
              <span class="tag is-success ml-2">
                <b-icon icon="check" size="is-small" class="mr-1" />
                Espace connecté
              </span>
            </h2>
            <p>
              Créez et gérez vos classes virtuelles directement depuis Teachizy+,
              et retrouvez l'ensemble de vos classes virtuelles dans votre espace Talkizy.
            </p>
          </div>
          <p class="has-text-right">
            <b-button
              type="is-primary"
              tag="a"
              :href="talkizyURL"
              target="_blank"
              rel="noopener"
              icon-right="external-link-alt"
            >
              Accéder à mon espace Talkizy
            </b-button>
          </p>
        </div>

        <!-- Compte Talkizy connecté (owner ou admin membre) + espace actif. -->
        <div v-if="talkizyAccount" class="box mt-5">
          <div class="content">
            <h2 class="is-size-6">
              Compte et espace actif
            </h2>
          </div>

          <div class="columns">
            <div class="column">
              <p class="is-size-8 has-text-grey-dark mb-1">
                Titulaire du compte
              </p>
              <p class="mb-1">
                <strong>{{ talkizyAccount.firstname }} {{ talkizyAccount.lastname }}</strong>
              </p>
              <p class="is-size-8 has-text-grey-dark">
                {{ talkizyAccount.email }}
              </p>
            </div>
            <div class="column">
              <p class="is-size-8 has-text-grey-dark mb-1">
                Espace actif
              </p>
              <p class="mb-1">
                <strong>{{ activeTeam ? activeTeam.name : '—' }}</strong>
              </p>
              <p v-if="activeTeam && activeTeam.subdomain" class="is-size-8 has-text-grey-dark">
                {{ activeTeam.subdomain }}.talkizy.fr
              </p>
            </div>
          </div>

          <!-- Changement d'espace : réservé au owner, si plusieurs espaces. -->
          <div v-if="canSwitchTeam" class="btop-grey pt-4 mt-2">
            <p class="is-size-8 has-text-grey-dark mb-2">
              Changer l'espace actif
            </p>

            <!-- Bloqué tant que des classes sont en cours ou à venir. -->
            <b-notification
              v-if="activeTeamHasPendingMeetings"
              type="is-warning is-light"
              :closable="false"
              class="is-size-8 mb-3"
            >
              <b-icon icon="info-circle" size="is-small" class="mr-1" />
              Vous ne pouvez pas changer d'espace tant que des classes virtuelles sont
              en cours ou à venir sur l'espace actif. Terminez-les ou annulez-les d'abord.
            </b-notification>

            <div class="is-flex is-align-items-center is-flex-wrap-wrap">
              <b-select
                v-model="selectedTeamId"
                :disabled="isSwitchingTeam || activeTeamHasPendingMeetings"
                class="mr-3 mb-2"
              >
                <option
                  v-for="t in teamOptions"
                  :key="t.id"
                  :value="t.id"
                >
                  {{ t.name }}{{ t.is_current ? ' (actif)' : '' }}
                </option>
              </b-select>
              <b-button
                type="is-danger"
                outlined
                :loading="isSwitchingTeam"
                :disabled="!activeTeam || selectedTeamId === activeTeam.id || activeTeamHasPendingMeetings"
                class="mb-2"
                @click="confirmSwitchTeam"
              >
                Changer d'espace
              </b-button>
            </div>
          </div>
        </div>

        <!-- Détail des droits du plan Talkizy actuel (fonctionnalités + seuils). -->
        <div v-if="talkizyPlanDetail" class="box mt-5">
          <div class="content">
            <h2 class="is-size-6">
              Votre forfait Talkizy
            </h2>
            <p v-if="teachizyGrantedTier">
              Votre abonnement Teachizy <strong>{{ teachizyGrantedTier }}</strong> vous
              donne accès à un forfait Talkizy dédié,
              <strong>{{ talkizyPlanDetail.name }}</strong>. Voici les fonctionnalités
              et les seuils qu'il inclut.
            </p>
            <p v-else>
              Vous utilisez actuellement le forfait Talkizy <strong>{{ talkizyPlanDetail.name }}</strong>.
              Voici les fonctionnalités et les seuils qu'il inclut.
            </p>
          </div>

          <div class="columns">
            <div class="column">
              <h4 class="title is-7 mb-3">
                Seuils
              </h4>
              <ul>
                <li
                  v-for="item in planThresholds"
                  :key="item.key"
                  class="is-flex is-justify-content-space-between is-size-8 py-2 bbottom-grey"
                >
                  <span class="has-text-grey-dark">{{ item.label }}</span>
                  <strong class="ml-3 has-text-right">{{ item.display }}</strong>
                </li>
              </ul>
            </div>
            <div class="column">
              <h4 class="title is-7 mb-3">
                Fonctionnalités
              </h4>
              <ul>
                <li
                  v-for="item in planFeatures"
                  :key="item.key"
                  class="is-flex is-align-items-center is-size-8 py-2"
                >
                  <b-icon
                    :icon="item.included ? 'check' : 'times'"
                    size="is-small"
                    class="mr-2"
                    :class="item.included ? 'has-text-success' : 'has-text-grey-light'"
                  />
                  <span :class="{ 'has-text-grey-light': !item.included }">{{ item.label }}</span>
                </li>
              </ul>
            </div>
          </div>
        </div>

        <TalkizyDisconnectSection />

      </div>
    </GoProWrapper>
  </div>
</template>

<style lang="scss" scoped>
.cvparams {
  &_connected {
    max-width: 920px;
    margin: 0 auto;
  }
}
</style>
